// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Patient session store. Holds ONLY non-PHI session metadata — the opaque session
// JWT (an access credential, not PHI) and the opaque conversation id. Per HIPAA
// hygiene the frontend never persists PHI; the patient's snapshot lives server-side
// and is resolved by ai-gateway from the conversation id. Uses sessionStorage so the
// credential dies with the tab, and carries a soft TTL so a stale tab re-authenticates.

const JWT_KEY = 'qh_session_jwt'
const JWT_EXP_KEY = 'qh_session_exp'
const CONVERSATION_KEY = 'qh_conversation_id'
const DEEPLINK_TOKEN_KEY = 'qh_deeplink_token'
// The kiosk lock token that established the CURRENT session. Lets the kiosk welcome
// distinguish a genuine new hand-off (different token → fresh patient/encounter, reset
// everything) from a harmless re-mount of the same, already-consumed token.
const KIOSK_LOCK_KEY = 'qh_kiosk_kt'

// Session JWTs are issued with a 2h server-side TTL; we mirror it client-side so a
// long-idle tab routes back to verify instead of firing calls with a dead token.
const SESSION_TTL_MS = 2 * 60 * 60 * 1000

export interface PatientSession {
  jwt: string
  /** epoch ms after which the client treats the session as expired */
  expiresAt: number
}

export function setSession(jwt: string, ttlMs: number = SESSION_TTL_MS): void {
  const expiresAt = Date.now() + ttlMs
  sessionStorage.setItem(JWT_KEY, jwt)
  sessionStorage.setItem(JWT_EXP_KEY, String(expiresAt))
}

export function getSession(): PatientSession | null {
  const jwt = sessionStorage.getItem(JWT_KEY)
  const expRaw = sessionStorage.getItem(JWT_EXP_KEY)
  if (!jwt || !expRaw) return null

  const expiresAt = Number(expRaw)
  if (!Number.isFinite(expiresAt) || Date.now() >= expiresAt) {
    clearSession()
    return null
  }
  return { jwt, expiresAt }
}

export type SessionState = 'valid' | 'expired' | 'absent'

// Non-destructive session probe for route guards. Unlike getSession() this never clears
// storage, and it distinguishes 'expired' (the patient got through verify but their 2h
// window lapsed → needs a new link) from 'absent' (never verified in this tab).
export function getSessionState(): SessionState {
  const jwt = sessionStorage.getItem(JWT_KEY)
  const expRaw = sessionStorage.getItem(JWT_EXP_KEY)
  if (!jwt || !expRaw) return 'absent'
  const expiresAt = Number(expRaw)
  if (!Number.isFinite(expiresAt) || Date.now() >= expiresAt) return 'expired'
  return 'valid'
}

export function getSessionToken(): string | null {
  return getSession()?.jwt ?? null
}

export function hasValidSession(): boolean {
  return getSession() !== null
}

// Tears down the whole patient session: the JWT, its mirrored expiry, the in-memory
// first name, and the opaque conversation + deeplink keys. Leaving those behind after a
// logout/expiry strands stale identifiers that would misroute the next flow in the tab.
export function clearSession(): void {
  sessionStorage.removeItem(JWT_KEY)
  sessionStorage.removeItem(JWT_EXP_KEY)
  sessionStorage.removeItem(CONVERSATION_KEY)
  sessionStorage.removeItem(DEEPLINK_TOKEN_KEY)
  sessionStorage.removeItem(KIOSK_LOCK_KEY)
  patientFirstName = null
}

// ── Kiosk lock token that minted the current session (opaque; not PHI) ─────────
// Used only to detect a NEW kiosk hand-off vs a re-mount of the same token.

export function setKioskLockToken(kt: string): void {
  sessionStorage.setItem(KIOSK_LOCK_KEY, kt)
}

export function getKioskLockToken(): string | null {
  return sessionStorage.getItem(KIOSK_LOCK_KEY)
}

// ── Patient first name (PHI — in-memory only, never persisted) ─────────────────
// Held in a module variable, not sessionStorage, so it dies with the page and is never
// written to disk. Used only to personalize the UI; re-fetched from the session on
// verify/kiosk redeem. Lost on reload (cosmetic only) — that's the safe trade.

let patientFirstName: string | null = null

export function setPatientFirstName(name: string): void {
  patientFirstName = name
}

export function getPatientFirstName(): string | null {
  return patientFirstName
}

// ── Deeplink token (opaque HMAC access token; not PHI) ─────────────────────────
// The self-eval deeplink lands at `/?token=<hmac>`. Capture it once at app entry and
// keep it for /verify (which exchanges token + DOB for a session via verify-token).

const DEEPLINK_TOKEN_PARAM = 'token'

// 'expired' is a routing sentinel (WelcomeScreen redirects to /link-expired), not a
// real credential — leave it in the URL and don't persist it as a usable token.
const DEEPLINK_TOKEN_SENTINELS = new Set(['expired'])

// Read `?token=` from the current URL, stash the opaque deeplink token for the verify
// step, and strip it from the address bar. The token is a bearer credential (token + DOB
// mints a PHI session), so it must not linger in the URL where it leaks via browser
// history, the Referer header, CDN/proxy logs, and shoulder-surfing.
export function captureDeeplinkTokenFromUrl(): void {
  const url = new URL(window.location.href)
  const token = url.searchParams.get(DEEPLINK_TOKEN_PARAM)
  if (!token || DEEPLINK_TOKEN_SENTINELS.has(token)) return
  sessionStorage.setItem(DEEPLINK_TOKEN_KEY, token)
  url.searchParams.delete(DEEPLINK_TOKEN_PARAM)
  window.history.replaceState({}, '', url.pathname + url.search + url.hash)
}

export function getDeeplinkToken(): string | null {
  return sessionStorage.getItem(DEEPLINK_TOKEN_KEY)
}

export function clearDeeplinkToken(): void {
  sessionStorage.removeItem(DEEPLINK_TOKEN_KEY)
}

// ── Conversation id (opaque; not PHI) ─────────────────────────────────────────

export function setConversationId(id: string): void {
  sessionStorage.setItem(CONVERSATION_KEY, id)
}

export function getConversationId(): string | null {
  return sessionStorage.getItem(CONVERSATION_KEY)
}

export function clearConversationId(): void {
  sessionStorage.removeItem(CONVERSATION_KEY)
}
